// mds-dojo — dijit/form/ComboBoxMixin — FAITHFUL port of references/dijit/form/ComboBoxMixin.js.
// Codemod pipeline; declare from the faithful runtime. Adaptations:
//  - `dojo/text!./templates/DropDownBox.html` → `import template from './templates/DropDownBox.html?raw'`
//    (template .html copied from references/dijit/form/templates/).
//  - `dojo/Deferred` → `../../dojo/_base/Deferred` (mds has no top-level dojo/Deferred alias).
//  - `kernel.deprecated` shimmed to no-op when the slim mds kernel lacks it.
//  - legacy AMD `require(["dojo/data/ObjectStore"], …)` inside the deprecated store.fetch() shim:
//    mds has no AMD require and no dojo/data/ObjectStore, so that dead back-compat branch (only
//    reachable if user code calls the OLD dojo.data fetch() API on the builtin store) is replaced
//    by a console.error. The deprecated() warning fires exactly as in real dojo first. FLAGGED.
import declare from '../../dojo/_base/declare-runtime'; // declare
import Deferred from '../../dojo/_base/Deferred';
import kernel from '../../dojo/_base/kernel'; // kernel.deprecated
import lang from '../../dojo/_base/lang'; // lang.mixin
import QueryResults from '../../dojo/store/util/QueryResults';
import _AutoCompleterMixin from './_AutoCompleterMixin';
import _ComboBoxMenu from './_ComboBoxMenu';
import _HasDropDown from '../_HasDropDown';
import template from './templates/DropDownBox.html?raw';

var deprecated: any = (kernel as any).deprecated || function () { /* no-op shim */ };

export default declare("dijit.form.ComboBoxMixin", [_HasDropDown, _AutoCompleterMixin], {
	// summary:
	//		Provides main functionality of ComboBox widget

	// dropDownClass: [protected extension] Function String
	//		Dropdown widget class used to select a date/time.
	//		Subclasses should specify this.
	dropDownClass: _ComboBoxMenu,

	// hasDownArrow: Boolean
	//		Set this textbox to have a down arrow button, to display the drop down list.
	//		Defaults to true.
	hasDownArrow: true,

	templateString: template,

	baseClass: "dijitTextBox dijitComboBox",

	// Set classes like dijitDownArrowButtonHover depending on
	// mouse action over button node
	cssStateNodes: {
		"_buttonNode": "dijitDownArrowButton"
	},

	_setHasDownArrowAttr: function (/*Boolean*/ val: any) {
		this._set("hasDownArrow", val);
		this._buttonNode.style.display = val ? "" : "none";
	},

	_showResultList: function () {
		// hide the tooltip
		this.displayMessage("");
		this.inherited(arguments);
	},

	_setStoreAttr: function (store: any) {
		// For backwards-compatibility, accept dojo.data store in addition to dojo/store/api/Store.  Remove in 2.0.
		if (!store.get) {
			lang.mixin(store, {
				_oldAPI: true,
				get: function (id: any) {
					// summary:
					//		Retrieves an object by it's identity. This will trigger a fetchItemByIdentity.
					//		Like dojo/store/DataStore.get() except returns native item.
					var deferred = new Deferred();
					this.fetchItemByIdentity({
						identity: id,
						onItem: function (object: any) {
							deferred.resolve(object);
						},
						onError: function (error: any) {
							deferred.reject(error);
						}
					});
					return deferred.promise;
				},
				query: function (query: any, options: any) {
					// summary:
					//		Queries the store for objects.   Like dojo/store/DataStore.query()
					//		except returned Deferred contains array of native items.
					var deferred = new Deferred(function () { fetchHandle.abort && fetchHandle.abort(); });
					deferred.total = new Deferred();
					var fetchHandle = this.fetch(lang.mixin({
						query: query,
						onBegin: function (count: any) {
							deferred.total.resolve(count);
						},
						onComplete: function (results: any) {
							deferred.resolve(results);
						},
						onError: function (error: any) {
							deferred.reject(error);
						}
					}, options));
					return QueryResults(deferred);
				}
			});
		}
		this._set("store", store);
	},

	postMixInProperties: function () {
		// Since _setValueAttr() depends on this.store, _setStoreAttr() needs to execute first.
		// Unfortunately, without special code, it ends up executing second.
		var store = this.params.store || this.store;
		if (store) {
			this._setStoreAttr(store);
		}

		this.inherited(arguments);

		// User may try to access this.store.getValue() etc.  in a custom labelFunc() function.
		// It's not available with the new data store for handling inline <option> tags, so add it.
		if (!this.params.store && this.store && !this.store._oldAPI) {
			var clazz = this.declaredClass;
			lang.mixin(this.store, {
				getValue: function (item: any, attr: any) {
					deprecated(clazz + ".store.getValue(item, attr) is deprecated for builtin store.  Use item.attr directly", "", "2.0");
					return item[attr];
				},
				getLabel: function (item: any) {
					deprecated(clazz + ".store.getLabel(item) is deprecated for builtin store.  Use item.label directly", "", "2.0");
					return item.name;
				},
				fetch: function (args: any) {
					deprecated(clazz + ".store.fetch() is deprecated for builtin store.", "Use store.query()", "2.0");
					// Real dojo lazily require()'d dojo/data/ObjectStore here. mds has no AMD require
					// nor dojo/data/ObjectStore — this legacy dojo.data fetch() path is unsupported.
					console.error(clazz + ".store.fetch(): the legacy dojo/data ObjectStore fetch path is not available in mds-dojo; use store.query().");
				}
			});
		}
	},

	buildRendering: function () {
		this.inherited(arguments);

		this.focusNode.setAttribute("aria-autocomplete", this.autoComplete ? "both" : "list");
	}
});
